import { createFileRoute, Link } from "@tanstack/react-router";
import { Activity, AlertTriangle, MousePointerClick, Users } from "lucide-react";
import { PageHeader } from "@/client/components/page-header";
import { Card } from "@/client/components/ui/card";
import { MiniBarRow } from "@/client/components/ui/mini-bar-row";
import { apiQuery, useApiSuspenseQuery } from "@/client/lib/api";
import { ensurePage, pageHead } from "@/client/lib/page";
import { cn } from "@/shared/utils";

/** Aba Métricas — só para o admin da plataforma Etimos. Nada de dado de paciente. */
export const Route = createFileRoute("/app/metricas")({
  validateSearch: (s: Record<string, unknown>): { dias?: string } => ({ dias: s.dias as string | undefined }),
  loaderDeps: ({ search }) => search,
  loader: ({ context, deps }) => ensurePage(context.queryClient, apiQuery("metricas", "page", deps)),
  head: () => pageHead("Métricas"),
  component: MetricasPage,
});

const nf = new Intl.NumberFormat("pt-BR");

function Ranking({
  title,
  icon,
  rows,
}: {
  title: string;
  icon: React.ReactNode;
  rows: { name: string; count: number }[];
}) {
  const max = Math.max(1, ...rows.map((r) => r.count));
  return (
    <Card className="p-5">
      <h2 className="mb-4 flex items-center gap-2 text-sm font-semibold text-slate-800">
        {icon}
        {title}
      </h2>
      {rows.length === 0 ? (
        <p className="text-sm text-slate-500">Sem registros no período.</p>
      ) : (
        <div className="flex flex-col gap-2.5">
          {rows.map((r) => (
            <MiniBarRow
              key={r.name}
              icon={null}
              label={r.name}
              value={r.count}
              formatted={nf.format(r.count)}
              percent={(r.count / max) * 100}
            />
          ))}
        </div>
      )}
    </Card>
  );
}

function MetricasPage() {
  const search = Route.useSearch();
  const { data } = useApiSuspenseQuery("metricas", "page", search);
  const maxDay = Math.max(1, ...data.activeByDay.map((d) => d.users));
  return (
    <div className="mx-auto flex max-w-7xl flex-col gap-6">
      <PageHeader title="Métricas" description="Uso do app e últimos erros. Visível só para a equipe da Etimos." />
      <div className="flex gap-1 self-start rounded-xl border border-slate-200 bg-white p-1 shadow-soft">
        {[7, 30, 90].map((d) => (
          <Link
            key={d}
            to="/app/metricas"
            search={{ dias: String(d) }}
            className={cn(
              "rounded-lg px-3 py-1.5 text-xs font-medium transition",
              data.days === d ? "bg-brand-700 text-white" : "text-slate-600 hover:bg-slate-100",
            )}
          >
            {d} dias
          </Link>
        ))}
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        <Card className="p-4">
          <p className="text-xs text-slate-500">Eventos no período</p>
          <p className="font-display text-2xl text-slate-900">{nf.format(data.totals.events)}</p>
        </Card>
        <Card className="p-4">
          <p className="text-xs text-slate-500">Usuários ativos</p>
          <p className="font-display text-2xl text-slate-900">{nf.format(data.totals.users)}</p>
        </Card>
        <Card className="p-4">
          <p className="text-xs text-slate-500">Erros abertos (14 dias)</p>
          <p className="font-display text-2xl text-slate-900">{data.issues ? nf.format(data.issues.length) : "—"}</p>
        </Card>
      </div>

      <Card className="p-5">
        <h2 className="mb-4 flex items-center gap-2 text-sm font-semibold text-slate-800">
          <Users className="h-4 w-4 text-brand-700" /> Usuários ativos por dia
        </h2>
        {data.activeByDay.length === 0 ? (
          <p className="text-sm text-slate-500">Sem uso registrado no período.</p>
        ) : (
          <div className="flex h-32 items-end gap-1" role="img" aria-label="Usuários ativos por dia">
            {data.activeByDay.map((d) => (
              <div
                key={d.day}
                className="flex flex-1 flex-col items-center justify-end"
                title={`${d.day}: ${d.users} usuário(s), ${d.events} evento(s)`}
              >
                <div
                  className="w-full rounded-t bg-brand-700"
                  style={{ height: `${(d.users / maxDay) * 100}%`, minHeight: 2 }}
                />
              </div>
            ))}
          </div>
        )}
      </Card>

      <div className="grid gap-4 md:grid-cols-2">
        <Ranking
          title="Telas mais abertas"
          icon={<Activity className="h-4 w-4 text-brand-700" />}
          rows={data.screens}
        />
        <Ranking
          title="Ações mais feitas"
          icon={<MousePointerClick className="h-4 w-4 text-brand-700" />}
          rows={data.actions}
        />
      </div>

      <Card className="p-5">
        <h2 className="mb-4 flex items-center gap-2 text-sm font-semibold text-slate-800">
          <AlertTriangle className="h-4 w-4 text-amber-600" /> Últimos erros (Sentry)
        </h2>
        {data.issues === null ? (
          <p className="text-sm text-slate-500">
            Configure SENTRY_AUTH_TOKEN, SENTRY_ORG e SENTRY_PROJECT nos secrets do Worker para listar os erros aqui.
          </p>
        ) : data.issues.length === 0 ? (
          <p className="text-sm text-slate-500">Nenhum erro aberto nos últimos 14 dias.</p>
        ) : (
          <ul className="divide-y divide-slate-100">
            {data.issues.map((i) => (
              <li key={i.id} className="flex items-center justify-between gap-3 py-2.5 text-sm">
                <a
                  href={i.permalink}
                  target="_blank"
                  rel="noreferrer"
                  className="min-w-0 truncate font-medium text-slate-800 hover:underline"
                >
                  {i.title}
                </a>
                <span className="shrink-0 text-xs text-slate-500">{nf.format(i.count)}×</span>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}
